import { Link } from "react-router-dom";
import { Shell } from "../components/Shell";

/**
 * Términos de uso de la plataforma — PLANTILLA base.
 * Regula la relación entre la alcaldía, el ciudadano y MiPueblo.
 * Cada alcaldía debe adaptarla y aprobarla legalmente antes de publicar.
 */
export function DocsTerminosPage() {
  return (
    <Shell>
      <section className="page" style={{ maxWidth: 880 }}>
        <div className="eyebrow">
          <Link to="/docs">← Documentación</Link> · Términos de uso
        </div>

        <h1 style={{ margin: "8px 0 12px", fontSize: 32 }}>
          Términos de uso de la plataforma
        </h1>

        <div className="callout warn">
          <strong>Esto es una PLANTILLA.</strong> Cada alcaldía debe adaptarla
          a su contexto y aprobarla con su área jurídica antes de publicarla en
          su portal ciudadano. MiPueblo no presta asesoría legal.
        </div>

        <div className="callout" style={{ marginTop: 12 }}>
          Los presentes términos regulan la relación entre la{" "}
          <strong>Alcaldía de [Municipio]</strong>, los <strong>ciudadanos</strong>{" "}
          que utilizan la plataforma y <strong>MiPueblo S.A.S.</strong> como
          proveedor tecnológico (encargado del tratamiento).
        </div>

        {/* ---- 1 ---- */}
        <Section title="1. Aceptación de los términos">
          <p>
            Al acceder, registrarse o utilizar la plataforma disponible en{" "}
            <code>{`{slug}.mipuebloapp.com`}</code>, el usuario manifiesta que
            ha leído, comprendido y aceptado en su totalidad los presentes
            términos de uso, así como la política de privacidad y la política
            de retención de datos. Si no está de acuerdo con cualquiera de
            estas condiciones, debe abstenerse de utilizar la plataforma.
          </p>
          <p>
            La aceptación se evidencia mediante el marcado del checkbox
            correspondiente al momento del primer uso y queda registrada en el
            audit log de la plataforma con fecha, hora, IP y versión aceptada.
          </p>
        </Section>

        {/* ---- 2 ---- */}
        <Section title="2. Definiciones">
          <Table
            head={["Término", "Definición"]}
            rows={[
              [
                "Plataforma",
                "Sistema web operado por MiPueblo S.A.S. y puesto a disposición de la alcaldía para la gestión de PQRSDF y atención ciudadana.",
              ],
              [
                "Tenant",
                "Espacio aislado de la plataforma asignado a una alcaldía específica, identificado por un slug y un subdominio propio.",
              ],
              [
                "Usuario",
                "Cualquier persona que accede a la plataforma, ya sea funcionario o ciudadano.",
              ],
              [
                "Funcionario",
                "Servidor público o contratista de la alcaldía con credenciales para gestionar peticiones, configurar el tenant o atender el chat.",
              ],
              [
                "Ciudadano",
                "Persona natural o jurídica que utiliza la plataforma para presentar PQRSDF o consultar información pública.",
              ],
              [
                "Peticionario",
                "Ciudadano que ha radicado formalmente una PQRSDF y a quien corresponde el derecho de respuesta.",
              ],
              [
                "PQRSDF",
                "Peticiones, Quejas, Reclamos, Sugerencias, Denuncias y Felicitaciones, según la Ley 1755 de 2015.",
              ],
              [
                "Radicado",
                "Número único e irrepetible asignado a cada PQRSDF al momento de su recepción formal.",
              ],
            ]}
          />
        </Section>

        {/* ---- 3 ---- */}
        <Section title="3. Uso permitido y uso prohibido">
          <p>
            <strong>Uso permitido:</strong> radicar PQRSDF legítimas, consultar
            su estado, interactuar con la alcaldía a través de los canales
            habilitados, descargar respuestas y certificaciones propias.
          </p>
          <p>
            <strong>Queda expresamente prohibido:</strong>
          </p>
          <Ul
            items={[
              "Radicar peticiones masivas, automatizadas o repetidas con el fin de saturar el servicio (spam).",
              "Suministrar datos falsos, suplantar la identidad de otra persona o usar documentos de identidad ajenos.",
              "Cargar contenido ofensivo, discriminatorio, calumnioso, injurioso, obsceno o que incite a la violencia.",
              "Cargar archivos con malware, virus, scripts maliciosos o cualquier código que pueda afectar la integridad de la plataforma.",
              "Utilizar la plataforma para fines distintos a los previstos en estos términos (publicidad, propaganda política, comercio).",
              "Intentar acceder a información de otros ciudadanos, otros tenants o áreas restringidas de la plataforma.",
              "Realizar pruebas de penetración, ingeniería inversa o cualquier actividad que comprometa la seguridad sin autorización escrita.",
              "Vulnerar derechos de propiedad intelectual de terceros.",
            ]}
          />
        </Section>

        {/* ---- 4 ---- */}
        <Section title="4. Obligaciones del ciudadano al radicar">
          <Ul
            items={[
              "Suministrar información veraz, exacta y actualizada.",
              "Identificarse adecuadamente cuando el trámite así lo requiera.",
              "Mantener la confidencialidad de las credenciales de acceso si crea una cuenta.",
              "No suplantar la identidad de otras personas.",
              "Respetar a los funcionarios que atienden la solicitud, absteniéndose de expresiones ofensivas.",
              "Aportar los soportes documentales necesarios para la atención de su solicitud.",
              "Mantener actualizados sus datos de contacto para recibir las notificaciones.",
            ]}
          />
        </Section>

        {/* ---- 5 ---- */}
        <Section title="5. Obligaciones de la alcaldía">
          <p>
            La alcaldía, en su condición de entidad pública, se obliga a:
          </p>
          <Ul
            items={[
              <>
                Dar respuesta a las peticiones dentro de los plazos establecidos
                en la <strong>Ley 1755 de 2015</strong>:
              </>,
            ]}
          />
          <Table
            head={["Tipo de petición", "Plazo de respuesta"]}
            rows={[
              ["Petición general", "15 días hábiles"],
              ["Solicitud de documentos e información", "10 días hábiles"],
              ["Consultas a autoridades", "30 días hábiles"],
              ["Quejas y reclamos", "15 días hábiles"],
              [
                "Trámites con plazo especial",
                "El que indique la norma específica",
              ],
            ]}
          />
          <Ul
            items={[
              "Tratar los datos personales del ciudadano conforme a la Ley 1581/2012 y la política de privacidad.",
              "Garantizar la trazabilidad de cada PQRSDF mediante un número de radicado único.",
              "Capacitar a sus funcionarios en el uso adecuado de la plataforma.",
              "Mantener actualizada la información de dependencias, horarios y contactos en su tenant.",
              "Cumplir las directrices de archivo y conservación documental (Ley 594/2000).",
            ]}
          />
        </Section>

        {/* ---- 6 ---- */}
        <Section title="6. Limitación de responsabilidad de MiPueblo">
          <p>
            MiPueblo S.A.S. presta el servicio tecnológico bajo el modelo
            Software as a Service (SaaS). En ningún caso será responsable por:
          </p>
          <Ul
            items={[
              "El contenido de las PQRSDF radicadas por los ciudadanos.",
              "El sentido, oportunidad o calidad de las respuestas dadas por la alcaldía.",
              "Decisiones administrativas adoptadas por la alcaldía con base en información gestionada en la plataforma.",
              "Pérdidas indirectas, lucro cesante o daños consecuenciales derivados del uso o la imposibilidad de uso del servicio.",
              "Interrupciones causadas por fuerza mayor, caso fortuito, fallas de terceros proveedores (ISP, hosting, API de WhatsApp, etc.) o ataques informáticos no atribuibles a negligencia de MiPueblo.",
              "Uso indebido de la plataforma por parte de funcionarios o ciudadanos.",
            ]}
          />
          <p>
            La responsabilidad de MiPueblo se limita, en todo caso, al
            cumplimiento del contrato de prestación de servicios suscrito con la
            alcaldía y a los compromisos del Acuerdo de Nivel de Servicio (SLA).
          </p>
        </Section>

        {/* ---- 7 ---- */}
        <Section title="7. Propiedad intelectual">
          <Ul
            items={[
              <>
                <strong>Plataforma:</strong> el software, código fuente, diseño,
                interfaces, marca y documentación son propiedad exclusiva de
                MiPueblo S.A.S. y están protegidos por las leyes de propiedad
                intelectual aplicables.
              </>,
              <>
                <strong>Marca y branding de la alcaldía:</strong> permanecen de
                titularidad exclusiva de la alcaldía. MiPueblo solo los utiliza
                dentro del tenant correspondiente y mientras dure el contrato.
              </>,
              <>
                <strong>Contenido aportado por el ciudadano</strong>{" "}
                (descripciones, adjuntos, fotografías): sigue siendo de su
                titularidad. El ciudadano otorga a la alcaldía una licencia no
                exclusiva, gratuita y por el tiempo necesario para atender,
                archivar y reportar la PQRSDF.
              </>,
              <>
                <strong>Plantillas de respuesta, artículos del centro de
                ayuda y configuraciones</strong> creadas por la alcaldía son de
                su propiedad y pueden ser exportadas al finalizar el contrato.
              </>,
              <>
                <strong>Datos estadísticos agregados y anonimizados</strong>{" "}
                pueden ser utilizados por MiPueblo con fines de mejora del
                servicio, investigación y benchmarking sectorial.
              </>,
            ]}
          />
        </Section>

        {/* ---- 8 ---- */}
        <Section title="8. Disponibilidad del servicio">
          <p>
            MiPueblo se compromete a mantener una disponibilidad mensual
            objetivo (SLA) de <strong>99.5%</strong>, calculada sobre el total
            de minutos del mes calendario, excluyendo las ventanas de
            mantenimiento programado.
          </p>
          <Table
            head={["Concepto", "Detalle"]}
            rows={[
              ["SLA objetivo", "99.5% mensual"],
              [
                "Ventanas de mantenimiento programado",
                "Domingo entre 02:00 y 06:00 (hora Colombia), con preaviso de 48 horas",
              ],
              [
                "Mantenimientos de emergencia",
                "Pueden ejecutarse sin preaviso ante riesgos de seguridad o estabilidad",
              ],
              [
                "Exclusiones del SLA",
                "Fuerza mayor, fallas de terceros proveedores, ataques DDoS, mal uso del cliente",
              ],
              [
                "Soporte",
                "Lunes a viernes 8:00-18:00 hora Colombia, vía panel y correo de soporte",
              ],
            ]}
          />
        </Section>

        {/* ---- 9 ---- */}
        <Section title="9. Suspensión de cuentas">
          <p>
            La alcaldía, a través de su admin_tenant, o MiPueblo cuando exista
            riesgo para la integridad del servicio, podrán suspender cuentas o
            bloquear accesos en los siguientes casos:
          </p>
          <Ul
            items={[
              "Incumplimiento reiterado de los presentes términos.",
              "Uso de la plataforma para fines fraudulentos o ilegales.",
              "Cargas masivas que comprometan el rendimiento.",
              "Suplantación de identidad comprobada.",
              "Requerimiento de autoridad competente.",
              "Falta de pago del contrato suscrito con la alcaldía (en este caso solo aplica al tenant completo, no a ciudadanos).",
            ]}
          />
          <p>
            La suspensión se notificará al titular afectado y, cuando proceda,
            se le indicará el procedimiento para subsanar.
          </p>
        </Section>

        {/* ---- 10 ---- */}
        <Section title="10. Modificaciones a los términos">
          <p>
            La alcaldía y MiPueblo podrán modificar los presentes términos en
            cualquier momento. Los cambios sustanciales serán comunicados con
            al menos <strong>10 días calendario</strong> de anticipación a su
            entrada en vigencia, mediante aviso en el portal ciudadano y por
            los canales de notificación habituales. El uso continuado de la
            plataforma tras la entrada en vigencia constituye aceptación de los
            nuevos términos.
          </p>
        </Section>

        {/* ---- 11 ---- */}
        <Section title="11. Ley aplicable y jurisdicción">
          <p>
            Los presentes términos se rigen por las leyes de la{" "}
            <strong>República de Colombia</strong>. Cualquier controversia
            derivada de su interpretación, ejecución o terminación se someterá
            a la jurisdicción ordinaria de los jueces competentes del municipio
            de <strong>[Municipio sede de la alcaldía]</strong>, renunciando a
            cualquier otro fuero que pudiera corresponder.
          </p>
          <p>
            Las partes intentarán primero resolver las diferencias por la vía
            del arreglo directo dentro de los 30 días siguientes a la
            comunicación escrita de la controversia.
          </p>
        </Section>

        {/* ---- 12 ---- */}
        <Section title="12. Contacto">
          <Table
            rows={[
              [
                "Alcaldía de [Municipio]",
                "[correo institucional] · [teléfono] · [dirección]",
              ],
              [
                "MiPueblo S.A.S. (soporte técnico)",
                "soporte@mipuebloapp.com",
              ],
              [
                "Asuntos de protección de datos",
                "habeasdata@municipio.gov.co",
              ],
            ]}
          />
        </Section>

        <div className="callout warn" style={{ marginTop: 32 }}>
          <strong>Recordatorio final:</strong> esta plantilla es una base
          técnica. La alcaldía es responsable de adaptarla, aprobarla
          jurídicamente y publicarla. MiPueblo no presta asesoría legal.
        </div>
      </section>
    </Shell>
  );
}

/* ============ helpers de presentación ============ */

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section
      style={{
        marginTop: 32,
        paddingTop: 24,
        borderTop: "1px solid var(--hairline)",
      }}
    >
      <h2 style={{ fontSize: 20, marginTop: 0, marginBottom: 14 }}>{title}</h2>
      <div style={{ lineHeight: 1.6, color: "var(--ink-2)", fontSize: 14.5 }}>
        {children}
      </div>
    </section>
  );
}

function Ul({ items }: { items: React.ReactNode[] }) {
  return (
    <ul style={{ paddingLeft: 22, margin: "8px 0", display: "grid", gap: 4 }}>
      {items.map((it, i) => (
        <li key={i}>{it}</li>
      ))}
    </ul>
  );
}

function Table({
  head,
  rows,
}: {
  head?: string[];
  rows: (string | React.ReactNode)[][];
}) {
  return (
    <div
      style={{
        marginTop: 10,
        border: "1px solid var(--hairline)",
        borderRadius: 8,
        overflow: "hidden",
      }}
    >
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13.5 }}>
        {head && (
          <thead>
            <tr style={{ background: "var(--bone-warm)" }}>
              {head.map((h, i) => (
                <th
                  key={i}
                  style={{
                    textAlign: "left",
                    padding: "8px 12px",
                    fontFamily: "var(--font-mono)",
                    fontSize: 10.5,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: "var(--ink-3)",
                    fontWeight: 500,
                    borderBottom: "1px solid var(--hairline)",
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={i}
              style={{ borderTop: i === 0 && !head ? 0 : "1px solid var(--hairline)" }}
            >
              {row.map((cell, j) => (
                <td
                  key={j}
                  style={{
                    padding: "10px 12px",
                    verticalAlign: "top",
                    fontWeight: j === 0 ? 600 : 400,
                  }}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
